/**
 * The quiz's end screen — §2.8 #72, #78.
 *
 * Extracted from `LessonQuizRunner`. Score, percentage, and the per-concept
 * review summary that says what the run actually did to the revision schedule.
 *
 * The percentage divides by the TOTAL question count, unlike the mid-run header
 * which divides by how many were answered. Both are right for where they sit: a
 * finished quiz has answered everything, so the two denominators converge —
 * except on a run restored from a session whose content was replaced, which is
 * exactly when a `correct/answered` figure here would flatter the result.
 */
import { RotateCcw, WalletCards } from 'lucide-react';
import { Button, cn } from '@ai-tutor/ui';
import {
  BlockMarkdown,
  InlineMarkdown,
} from '@/components/core/markdown/markdown';
import type { Question } from '@/data/fixtures';
import {
  answerText,
  correctAnswerText,
  type QuizAnswer,
} from '@/lib/practice/answerText';
import type { ConceptResolver } from '@/lib/practice/concepts';
import type {
  ConceptGrade,
  ReviewOutcomes,
  SubmissionState,
} from '@/lib/practice/grades';
import { ConceptReviewSummary } from './ConceptReviewSummary';
import { PRACTICE_CARD } from './practiceSurface';

export interface QuizCompletedViewProps {
  /** Total questions in the run — the percentage's denominator. */
  total: number;
  /** Questions answered correctly. */
  correct: number;
  /** One FSRS grade per concept, aggregated from the run's verdicts. */
  conceptGrades: ConceptGrade[];
  resolveConcept: ConceptResolver;
  outcomes: ReviewOutcomes;
  submission: SubmissionState;
  onRestart: () => void;
  /** Questions answered wrong, with what was given (absent on an old run). */
  mistakes?: { question: Question; given?: QuizAnswer }[];
  /** Go to the flashcards - offered as the next step. */
  onPractiseFlashcards?: () => void;
}

export function QuizCompletedView({
  total,
  correct,
  conceptGrades,
  resolveConcept,
  outcomes,
  submission,
  onRestart,
  mistakes = [],
  onPractiseFlashcards,
}: QuizCompletedViewProps) {
  const percentage = total > 0 ? Math.round((correct / total) * 100) : 0;

  return (
    <div className='mx-auto h-full max-w-xl overflow-y-auto p-4 sm:p-6'>
      <div
        className={cn(
          PRACTICE_CARD,
          'flex flex-col items-center p-6 text-center'
        )}
      >
        <h2 className='mb-1 text-2xl font-bold text-foreground'>Quiz done</h2>
        {/* The gradient is painted onto the text itself (`bg-clip-text` +
          `text-transparent`), so the figure has no computable foreground colour
          and screen readers get it from the text node alone — which is why the
          sentence below repeats it in plain words rather than relying on it. */}
        <p className='mb-1 bg-gradient-to-r from-brand-500 to-brand-600 bg-clip-text text-4xl font-bold text-transparent dark:from-primary dark:to-primary'>
          {percentage}%
        </p>
        <p className='mb-5 text-muted-foreground'>
          {correct} of {total} questions correct
        </p>

        <MistakesList mistakes={mistakes} />

        <ConceptReviewSummary
          grades={conceptGrades}
          resolveConcept={resolveConcept}
          outcomes={outcomes}
          submission={submission}
        />

        <div className='flex w-full flex-col gap-2 sm:flex-row-reverse'>
          {onPractiseFlashcards && (
            <Button
              onClick={onPractiseFlashcards}
              className='h-11 sm:h-9 sm:flex-1'
            >
              <WalletCards className='mr-2 h-4 w-4' aria-hidden='true' />
              Practise with flashcards
            </Button>
          )}
          <Button
            onClick={onRestart}
            variant={onPractiseFlashcards ? 'outline' : 'default'}
            className='h-11 sm:h-9 sm:flex-1'
          >
            <RotateCcw className='mr-2 h-4 w-4' aria-hidden='true' />
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * What was missed: each question, what was given, and the right answer -
 * the part of a quiz you learn from. The score alone said only how many.
 */
function MistakesList({
  mistakes,
}: {
  mistakes: { question: Question; given?: QuizAnswer }[];
}) {
  if (mistakes.length === 0) return null;
  return (
    <section aria-labelledby='quiz-mistakes' className='mb-5 w-full text-left'>
      <h3
        id='quiz-mistakes'
        className='mb-2 text-sm font-semibold text-foreground'
      >
        Review mistakes{' '}
        <span className='font-mono text-xs font-normal text-muted-foreground'>
          {mistakes.length}
        </span>
      </h3>
      <ul className='divide-y divide-border rounded-xl border border-border/60'>
        {mistakes.map(({ question, given }) => (
          <li key={question.id} className='p-3'>
            {/* Block, not inline: a question may carry a diagram or a table
                its answer depends on, and inline markdown drops it. */}
            <BlockMarkdown className='text-sm font-medium text-foreground [&_p]:my-0'>
              {question.question}
            </BlockMarkdown>
            {given !== undefined && (
              <p className='mt-1 text-sm text-muted-foreground'>
                You said:{' '}
                <span className='text-destructive-emphasis'>
                  <InlineMarkdown>{answerText(question, given)}</InlineMarkdown>
                </span>
              </p>
            )}
            <p className='mt-0.5 text-sm text-muted-foreground'>
              Answer:{' '}
              <span className='font-medium text-practice-emphasis'>
                <InlineMarkdown>{correctAnswerText(question)}</InlineMarkdown>
              </span>
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}
